import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../scripts/AuthContext";
import { useDmSocket } from "../scripts/DmSocketContext";
import {
  DM_TOMBSTONE_TTL_MS,
  deleteDmMessage,
  listDmMessages,
  markDmRead,
  sendDmImage,
  sendDmMedia,
  sendDmMessage,
  toggleDmReaction,
  updateDmMessage,
} from "@/lib/dmApi";
import {
  mergeServerMessages,
  messageIdKey,
  upsertMessage,
} from "@/lib/dmThreadMessages";

function mergeReactionMessage(prev, payload, currentUserId) {
  const incoming = payload?.message;
  if (!incoming) return prev;
  if (
    !payload?.emoji ||
    !payload?.user_id ||
    payload.user_id === currentUserId
  ) {
    return upsertMessage(prev, incoming);
  }
  const existing = prev.find(
    (m) => messageIdKey(m.message_id) === messageIdKey(incoming.message_id)
  );
  const prevReacted = new Map(
    (existing?.reactions || []).map((r) => [r.emoji, !!r.reacted])
  );
  const reactions = (incoming.reactions || []).map((r) => ({
    ...r,
    reacted: !!prevReacted.get(r.emoji),
  }));
  return upsertMessage(prev, { ...incoming, reactions });
}

function filterExpiredTombstones(messages, now = Date.now()) {
  return (messages || []).filter((m) => {
    if (!m?.deleted_at) return true;
    return now - new Date(m.deleted_at).getTime() < DM_TOMBSTONE_TTL_MS;
  });
}

export function useDmThread(orgId, conversationId, options = {}) {
  const { skipMessages = false } = options;
  const { accessToken } = useAuth();
  const { subscribe } = useDmSocket();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [tick, setTick] = useState(0);
  const [peerLastReadAt, setPeerLastReadAt] = useState(null);
  const markedReadRef = useRef(null);
  const messagesRef = useRef([]);
  const hasMoreOlderRef = useRef(true);
  const loadingOlderRef = useRef(false);
  const orgIdRef = useRef(orgId);
  const conversationIdRef = useRef(conversationId);
  const accessTokenRef = useRef(accessToken);
  const currentUserIdRef = useRef(null);
  const refreshGenRef = useRef(0);
  const activeThreadKeyRef = useRef(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    orgIdRef.current = orgId;
  }, [orgId]);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const currentUserId = useMemo(() => {
    if (!accessToken) return null;
    try {
      return jwtDecode(accessToken)?.sub || null;
    } catch {
      return null;
    }
  }, [accessToken]);

  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  const refresh = useCallback(async () => {
    if (!accessToken || !orgId || !conversationId) {
      activeThreadKeyRef.current = null;
      setMessages([]);
      setLoading(false);
      setAccessDenied(false);
      return;
    }
    if (skipMessages) {
      activeThreadKeyRef.current = `${orgId}:${conversationId}`;
      setMessages([]);
      setPreview(false);
      setAccessDenied(true);
      setLoading(false);
      setError(null);
      return;
    }
    const threadKey = `${orgId}:${conversationId}`;
    const threadSwitched = activeThreadKeyRef.current !== threadKey;
    activeThreadKeyRef.current = threadKey;
    const gen = ++refreshGenRef.current;
    // Drop prior-thread rows so merge cannot keep another channel's messages.
    if (threadSwitched) {
      setMessages([]);
      markedReadRef.current = null;
    }
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    hasMoreOlderRef.current = true;
    setHasMoreOlder(true);
    setPeerLastReadAt(null);
    try {
      const data = await listDmMessages(accessToken, orgId, conversationId, {
        limit: 80,
      });
      // Stale response from an older refresh — ignore.
      if (gen !== refreshGenRef.current) return;
      // Merge so an in-flight REST load cannot clobber a newer socket upsert
      // (common for webhook/deploy posts while the thread is already open).
      setMessages((prev) =>
        filterExpiredTombstones(mergeServerMessages(prev, data.messages))
      );
      setPreview(!!data.preview);
      setAccessDenied(false);
      if (data.peer_last_read_at !== undefined) {
        setPeerLastReadAt(data.peer_last_read_at);
      }
    } catch (err) {
      if (gen !== refreshGenRef.current) return;
      console.error("Failed to load DM messages:", err);
      const status = err.response?.status;
      if (status === 403 || status === 401) {
        setMessages([]);
        setAccessDenied(true);
        setPreview(false);
        setError(null);
      } else {
        setError(err);
        setPreview(false);
        setAccessDenied(false);
      }
    } finally {
      if (gen === refreshGenRef.current) {
        setLoading(false);
      }
    }
  }, [accessToken, orgId, conversationId, skipMessages]);

  // Load an older page (prepended). Returns { loaded, hasMore } for callers
  // that page toward a specific message (e.g. jump-to-replied-message).
  const loadOlder = useCallback(async () => {
    if (!accessToken || !orgId || !conversationId) {
      return { loaded: 0, hasMore: false };
    }
    if (loadingOlderRef.current || !hasMoreOlderRef.current) {
      return { loaded: 0, hasMore: hasMoreOlderRef.current };
    }
    const oldest = messagesRef.current[0]?.message_id;
    if (!oldest) return { loaded: 0, hasMore: false };

    loadingOlderRef.current = true;
    try {
      const data = await listDmMessages(accessToken, orgId, conversationId, {
        limit: 80,
        beforeId: oldest,
      });
      const older = filterExpiredTombstones(data.messages);
      if (!older.length) {
        hasMoreOlderRef.current = false;
        setHasMoreOlder(false);
        return { loaded: 0, hasMore: false };
      }
      setMessages((prev) => {
        const ids = new Set(
          prev.map((m) => messageIdKey(m.message_id)).filter(Boolean)
        );
        const dedup = older.filter(
          (m) => !ids.has(messageIdKey(m.message_id))
        );
        return [...dedup, ...prev];
      });
      return { loaded: older.length, hasMore: true };
    } catch (err) {
      console.error("Failed to load older DM messages:", err);
      return { loaded: 0, hasMore: hasMoreOlderRef.current };
    } finally {
      loadingOlderRef.current = false;
    }
  }, [accessToken, orgId, conversationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Drop expired tombstones while thread is open
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const visibleMessages = useMemo(
    () => filterExpiredTombstones(messages),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, tick]
  );

  useEffect(() => {
    if (!accessToken || !orgId || !conversationId || preview) return undefined;
    if (markedReadRef.current === conversationId) return undefined;
    markedReadRef.current = conversationId;
    markDmRead(accessToken, orgId, conversationId).catch(() => {});
    return undefined;
  }, [accessToken, orgId, conversationId, visibleMessages.length, preview]);

  // Stable socket subscription via refs so we never miss dm.message during
  // org/conversation identity updates or effect resubscribe churn.
  useEffect(() => {
    return subscribe((event, payload) => {
      const activeOrgId = orgIdRef.current;
      const activeConversationId = conversationIdRef.current;
      if (!activeOrgId || String(payload?.org_id) !== String(activeOrgId)) {
        return;
      }
      if (
        Number(payload?.conversation_id) !== Number(activeConversationId)
      ) {
        return;
      }

      if (event === "dm.read") {
        const uid = currentUserIdRef.current;
        if (
          payload?.user_id &&
          uid &&
          String(payload.user_id) !== String(uid)
        ) {
          setPeerLastReadAt(payload.last_read_at || null);
        }
        return;
      }

      if (!payload?.message) return;

      if (event === "dm.message") {
        setMessages((prev) =>
          filterExpiredTombstones(upsertMessage(prev, payload.message))
        );
        const token = accessTokenRef.current;
        if (token) {
          markDmRead(token, activeOrgId, activeConversationId).catch(() => {});
        }
      } else if (event === "dm.message.reaction") {
        setMessages((prev) =>
          filterExpiredTombstones(
            mergeReactionMessage(prev, payload, currentUserIdRef.current)
          )
        );
      } else if (
        event === "dm.message.updated" ||
        event === "dm.message.deleted"
      ) {
        setMessages((prev) =>
          filterExpiredTombstones(upsertMessage(prev, payload.message))
        );
      }
    });
  }, [subscribe]);

  const send = useCallback(
    async ({ content, attachment, reply_to_message_id, references }) => {
      if (!accessToken || !orgId || !conversationId) return null;
      setSending(true);
      try {
        const msg = await sendDmMessage(accessToken, orgId, conversationId, {
          content,
          attachment,
          reply_to_message_id,
          references,
        });
        setMessages((prev) => upsertMessage(prev, msg));
        return msg;
      } finally {
        setSending(false);
      }
    },
    [accessToken, orgId, conversationId]
  );

  const sendImage = useCallback(
    async (file, content) => {
      if (!accessToken || !orgId || !conversationId) return null;
      setSending(true);
      try {
        const msg = await sendDmImage(
          accessToken,
          orgId,
          conversationId,
          file,
          content
        );
        setMessages((prev) => upsertMessage(prev, msg));
        return msg;
      } finally {
        setSending(false);
      }
    },
    [accessToken, orgId, conversationId]
  );

  const sendMedia = useCallback(
    async ({ files = [], teamFiles = [], content } = {}) => {
      if (!accessToken || !orgId || !conversationId) return null;
      setSending(true);
      try {
        const msg = await sendDmMedia(accessToken, orgId, conversationId, {
          files,
          teamFiles,
          content,
        });
        setMessages((prev) => upsertMessage(prev, msg));
        return msg;
      } finally {
        setSending(false);
      }
    },
    [accessToken, orgId, conversationId]
  );

  const editMessage = useCallback(
    async (messageId, content, references) => {
      if (!accessToken || !orgId || !conversationId) return null;
      const msg = await updateDmMessage(
        accessToken,
        orgId,
        conversationId,
        messageId,
        content,
        references
      );
      setMessages((prev) => upsertMessage(prev, msg));
      return msg;
    },
    [accessToken, orgId, conversationId]
  );

  const removeMessage = useCallback(
    async (messageId) => {
      if (!accessToken || !orgId || !conversationId) return null;
      const msg = await deleteDmMessage(
        accessToken,
        orgId,
        conversationId,
        messageId
      );
      setMessages((prev) =>
        filterExpiredTombstones(upsertMessage(prev, msg))
      );
      return msg;
    },
    [accessToken, orgId, conversationId]
  );

  const reactToMessage = useCallback(
    async (messageId, emoji) => {
      if (!accessToken || !orgId || !conversationId) return null;
      const msg = await toggleDmReaction(
        accessToken,
        orgId,
        conversationId,
        messageId,
        emoji
      );
      setMessages((prev) => upsertMessage(prev, msg));
      return msg;
    },
    [accessToken, orgId, conversationId]
  );

  return {
    messages: visibleMessages,
    loading,
    sending,
    error,
    preview,
    accessDenied,
    hasMoreOlder,
    peerLastReadAt,
    currentUserId,
    refresh,
    loadOlder,
    send,
    sendImage,
    sendMedia,
    editMessage,
    removeMessage,
    reactToMessage,
  };
}
